import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, ArrowDown, Columns3 } from 'lucide-react';
import { ContextMenu, Menu } from '@ofself/shell';
import { Cell } from './Collection';
import { Empty } from './Empty';
import './DataTable.css';

// THE TABLE — one for every section's rows. A column is described once:
//
//   { id, label, cell(row), value?(row), num?, min?, grow?, lock?, off?, asc?, keep?, className? }
//
// `value` makes it sortable (nulls always last); a number or a date sorts high
// first, words A to Z, unless `asc` says otherwise. `min` (px) and `grow` (fr)
// size it; `grow: 0` holds it at `min`. `lock` keeps it in place and always
// shown (the item's own column, its Open). `off` starts it hidden.
//
// Its head sorts on a press (again, the other way), drags to reorder, and
// right-clicks (or its Columns button) to show and hide columns. The order,
// what is shown and the sort are kept per table on this device. When the
// columns need more room than there is, the table scrolls sideways inside
// itself, never past the page.
//
// A page that sorts on the server passes `sort` with `onSort`: the rows come
// already in order, and a press on a head asks `onSort({ id, dir })` instead
// of sorting here. With `fit`, columns that carry a `keep` rank give way when
// the table is too narrow, the highest rank first, before it scrolls. `empty`
// is said under the head once there are no rows; `loading` draws quiet rows
// until there are. `sticky` holds the head at the top of its scroller, `wrap`
// lets long words run onto more lines.
//
// A table that picks passes `picked` (the keys ticked) with `onPick(key, row)`:
// each row leads with a checkbox, and the head's ticks every row or none
// (`onPickAll(on)`). Without `onOpen`, a press on the row ticks it too.
// `pickHeld` greys every box (nothing may be picked now).
//
// `dimOf(row)` fades a row that misses the page's find, without hiding it.

const GAP = 12;
const PAD = 14;
const PICK = 16;
const KEY = (id) => `nx-table:${id}`;

function readSaved(id) {
    try { return JSON.parse(localStorage.getItem(KEY(id))) || null; } catch { return null; }
}
function writeSaved(id, v) {
    try { localStorage.setItem(KEY(id), JSON.stringify(v)); } catch { /* private mode */ }
}

const firstDir = (col) => (col.asc ?? !col.num) ? 'asc' : 'desc';
const widthOf = (cols) => cols.reduce((n, c) => n + (c.min || 80), 0) + GAP * Math.max(0, cols.length - 1) + PAD * 2;

function compare(a, b) {
    if (a == null || a === '') return b == null || b === '' ? 0 : 1;
    if (b == null || b === '') return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

// the saved layout, made to fit the columns there are now: new ones join at
// the end, gone ones drop out
function settle(columns, saved, sort) {
    const ids = columns.map((c) => c.id);
    const order = [...(saved?.order || []).filter((id) => ids.includes(id)), ...ids.filter((id) => !(saved?.order || []).includes(id))];
    const hidden = saved?.hidden ? saved.hidden.filter((id) => ids.includes(id)) : columns.filter((c) => c.off).map((c) => c.id);
    const kept = saved?.sort && ids.includes(saved.sort.id) ? saved.sort : sort;
    return { order, hidden, sort: kept || null };
}

// THE ROOM: the table's own width, once it can be measured (null until then,
// and always in a test, where nothing is laid out)
function useRoom(ref) {
    const [room, setRoom] = useState(null);
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(([e]) => setRoom(Math.round(e.contentRect.width)));
        ro.observe(el);
        return () => ro.disconnect();
    }, [ref]);
    return room;
}

/** A row that presses like a button and can still hold a link. */
function PressRow({ current, picked, dim, inert, onPress, style, children }) {
    return (
        <div
            role="row" tabIndex={0} style={style} aria-selected={current || picked || undefined}
            className={`nx-dt__row${current ? ' is-current' : ''}${picked ? ' is-picked' : ''}${dim ? ' is-dim' : ''}${inert ? ' is-inert' : ''}`}
            onClick={onPress} onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onPress(); } }}
        >
            {children}
        </div>
    );
}

export default function DataTable({
    id, label, columns, rows, keyOf, sort: givenSort, onSort, currentKey, onOpen, actionsOf,
    fit = false, empty, loading = false, sticky = false, wrap = false, total,
    picked, onPick, onPickAll, pickHeld = false, dimOf,
}) {
    const picking = typeof onPick === 'function';
    const ticked = picked instanceof Set ? picked : new Set(picked || []);
    const controlled = typeof onSort === 'function';
    const [layout, setLayoutState] = useState(() => settle(columns, readSaved(id), givenSort));
    const [dragging, setDragging] = useState(null);
    const [over, setOver] = useState(null);
    const box = useRef(null);
    const room = useRoom(box);
    // a sort the server keeps is never saved here: the address holds it
    const setLayout = (next) => { setLayoutState(next); writeSaved(id, controlled ? { ...next, sort: null } : next); };

    const byId = useMemo(() => new Map(columns.map((c) => [c.id, c])), [columns]);
    const now = settle(columns, layout, givenSort);
    if (controlled) now.sort = givenSort || null;
    // locked columns hold their place: the first ones lead, the last ones close
    const locked = columns.filter((c) => c.lock);
    const leading = locked.filter((c) => columns.indexOf(c) === 0).map((c) => c.id);
    const closing = locked.filter((c) => !leading.includes(c.id)).map((c) => c.id);
    const middle = now.order.filter((cid) => !byId.get(cid).lock && !now.hidden.includes(cid));
    let shown = [...leading, ...middle, ...closing].map((cid) => byId.get(cid));
    // too narrow: the columns with a keep rank give way, the highest first
    if (fit && room != null) {
        const yielding = shown.filter((c) => c.keep != null).sort((a, b) => b.keep - a.keep);
        while (yielding.length && widthOf(shown) > room) {
            const gone = yielding.shift();
            shown = shown.filter((c) => c !== gone);
        }
    }

    const tracks = [picking ? `${PICK}px` : null, ...shown.map((c) => (c.grow === 0 ? `${c.min || 80}px` : `minmax(${c.min || 80}px, ${c.grow || 1}fr)`))].filter(Boolean).join(' ');
    const minWidth = widthOf(shown) + (picking ? PICK + GAP : 0);
    const fits = room != null && minWidth <= room;
    const style = { gridTemplateColumns: tracks };

    const sortCol = now.sort && byId.get(now.sort.id);
    const sorted = useMemo(() => {
        if (controlled || !sortCol?.value) return rows || [];
        const sign = now.sort.dir === 'asc' ? 1 : -1;
        return (rows || [])
            .map((r, i) => [r, sortCol.value(r), i])
            .sort(([, a, i], [, b, j]) => {
                // empty values stay last whichever way it runs
                if ((a == null || a === '') !== (b == null || b === '')) return compare(a, b);
                return sign * compare(a, b) || i - j;
            })
            .map(([r]) => r);
    }, [rows, sortCol, now.sort?.dir, controlled]);

    // the open row stays in view as it changes (a link, ↑/↓ on the page)
    useEffect(() => {
        if (currentKey == null) return;
        box.current?.querySelector('.nx-dt__row.is-current')?.scrollIntoView?.({ block: 'nearest' });
    }, [currentKey]);

    const sortBy = (col) => {
        if (!col.value) return;
        const dir = now.sort?.id === col.id ? (now.sort.dir === 'asc' ? 'desc' : 'asc') : firstDir(col);
        if (controlled) onSort({ id: col.id, dir });
        else setLayout({ ...now, sort: { id: col.id, dir } });
    };
    const toggle = (cid) => setLayout({ ...now, hidden: now.hidden.includes(cid) ? now.hidden.filter((h) => h !== cid) : [...now.hidden, cid] });
    const move = (from, to) => {
        if (from === to) return;
        const order = now.order.filter((cid) => cid !== from);
        order.splice(order.indexOf(to) + (now.order.indexOf(from) < now.order.indexOf(to) ? 1 : 0), 0, from);
        setLayout({ ...now, order });
    };
    const reset = () => {
        try { localStorage.removeItem(KEY(id)); } catch { /* private mode */ }
        setLayoutState(settle(columns, null, givenSort));
    };

    const menu = [
        { header: 'Columns' },
        ...now.order.filter((cid) => !byId.get(cid).lock).map((cid) => ({
            label: byId.get(cid).label, checked: !now.hidden.includes(cid), onSelect: () => toggle(cid),
        })),
        { separator: true },
        { label: 'Reset columns', onSelect: reset },
    ];

    const head = (col, i) => {
        const on = now.sort?.id === col.id;
        const movable = !col.lock;
        const last = i === shown.length - 1;
        return (
            <div
                key={col.id} role="columnheader"
                aria-sort={on ? (now.sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                className={`nx-dt__th${col.num ? ' is-num' : ''}${over === col.id && dragging && dragging !== col.id ? ' is-over' : ''}${dragging === col.id ? ' is-dragging' : ''}`}
                draggable={movable}
                onDragStart={movable ? (ev) => { setDragging(col.id); ev.dataTransfer.effectAllowed = 'move'; ev.dataTransfer.setData('text/plain', col.id); } : undefined}
                onDragOver={movable && dragging ? (ev) => { ev.preventDefault(); setOver(col.id); } : undefined}
                onDrop={movable ? (ev) => { ev.preventDefault(); if (dragging) move(dragging, col.id); setDragging(null); setOver(null); } : undefined}
                onDragEnd={() => { setDragging(null); setOver(null); }}
            >
                {col.value
                    ? (
                        <button type="button" className={`nx-dt__sort${on ? ' is-on' : ''}`} onClick={() => sortBy(col)}>
                            {col.label}
                            {on && (now.sort.dir === 'asc' ? <ArrowUp aria-hidden="true" /> : <ArrowDown aria-hidden="true" />)}
                        </button>
                    )
                    : <span className="nx-dt__word">{col.label}</span>}
                {last && (
                    <Menu
                        items={menu}
                        align="end"
                        trigger={<button type="button" className="nx-dt__cols" aria-label="Columns" title="Columns"><Columns3 aria-hidden="true" /></button>}
                    />
                )}
            </div>
        );
    };

    const waiting = loading && !(rows && rows.length);
    return (
        <div
            ref={box} role="table" aria-label={label} aria-rowcount={total}
            className={`nx-dt${fits ? ' is-fits' : ''}${sticky ? ' is-sticky' : ''}${wrap ? ' is-wrapped' : ''}`}
        >
            <div className="nx-dt__body" style={{ minWidth }}>
                <ContextMenu
                    items={menu}
                    trigger={(
                        <div className="nx-dt__head" role="row" style={style}>
                            {picking && (
                                <div role="columnheader" className="nx-dt__th nx-dt__pick">
                                    <input
                                        type="checkbox" className="nx-dt__check" aria-label="Select all"
                                        checked={sorted.length > 0 && sorted.every((r) => ticked.has(keyOf(r)))}
                                        disabled={pickHeld || !sorted.length} onChange={(ev) => onPickAll?.(ev.target.checked)}
                                    />
                                </div>
                            )}
                            {shown.map(head)}
                        </div>
                    )}
                />
                {waiting && Array.from({ length: 8 }, (_, i) => (
                    <div key={i} className="nx-dt__row is-skel" style={style} aria-hidden="true">{picking && <i />}{shown.slice(0, 2).map((c) => <span key={c.id} />)}</div>
                ))}
                {!waiting && rows && rows.length === 0 && empty && <Empty className="nx-dt__empty">{empty}</Empty>}
                {sorted.map((r) => {
                    const key = keyOf(r);
                    return (
                        <Cell key={key} actions={actionsOf?.(r)}>
                            <PressRow
                                current={currentKey != null && String(key) === String(currentKey)} picked={picking && ticked.has(key)} dim={!!dimOf?.(r)} inert={!onOpen && !(picking && !pickHeld)} style={style}
                                onPress={() => (onOpen ? onOpen(key, r) : picking && !pickHeld && onPick(key, r))}
                            >
                                {picking && (
                                    <span role="cell" className="nx-dt__td nx-dt__pick">
                                        <input
                                            type="checkbox" className="nx-dt__check" aria-label="Select"
                                            checked={ticked.has(key)} disabled={pickHeld}
                                            onClick={(ev) => ev.stopPropagation()} onChange={() => onPick(key, r)}
                                        />
                                    </span>
                                )}
                                {shown.map((c) => (
                                    <span key={c.id} role="cell" className={`nx-dt__td${c.num ? ' is-num' : ''}${c.className ? ` ${c.className}` : ''}`}>{c.cell(r)}</span>
                                ))}
                            </PressRow>
                        </Cell>
                    );
                })}
                {/* the column rules: one grid over the rows, on their own tracks, so
                    each rule stands in the middle of the gap (none before a column
                    with no head, like an Open button's) */}
                {!waiting && sorted.length > 0 && (
                    <div className="nx-dt__rules" style={style} aria-hidden="true">
                        {picking && <span />}
                        {shown.map((c, i) => <span key={c.id} className={i > 0 && c.label ? 'is-ruled' : undefined} />)}
                    </div>
                )}
            </div>
        </div>
    );
}
